import { LocaleImage, LocaleAnchor } from '@/i18n/router';
import { LanguageSelector } from '@/i18n/LanguageSelector';
import { Localize } from '@/i18n/Localize';
import { sharedPortuguese } from '@/i18n/shared';
import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';

import { Link, useLocation } from '@/i18n/router';
import { ThemeToggle } from '@/components/ThemeToggle';
import AnnouncementBar from '@/components/AnnouncementBar';
import { CLOUD_APP_URL, CLOUD_SIGNUP_URL } from '@/data/cloud';
import { HARDCHARGER_DOCS_URL, HARDCHARGER_MIGRATION_URL } from '@/data/hardcharger';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface NavLink {
  label: string;
  to: string;
}

// "Products" menu collapses product + solution + service pages (incl. the new
// DuckDB-server / Arrow Flight SQL / Cut-Your-Bill landing pages).
const productGroups: { heading: string; links: NavLink[] }[] = [
  {
    heading: 'Products',
    links: [
      { label: 'Hardcharger', to: '/hardcharger' },
      { label: 'Install Hardcharger', to: '/hardcharger/install' },
      { label: 'Hardcharger Integrations', to: '/hardcharger/integrations' },
      { label: 'Chargewire', to: '/chargewire' },
      { label: 'GizmoData Cloud', to: '/cloud' },
      { label: 'Cloud at Customer', to: '/cloud/at-customer' },
      { label: 'GizmoEdge', to: '/gizmoedge' },
    ],
  },
  {
    heading: 'ADBC Drivers',
    links: [
      { label: 'All ADBC Drivers', to: '/adbc-drivers' },
      { label: 'Hardcharger Driver', to: '/adbc-drivers/hardcharger' },
      { label: 'GizmoSQL Driver', to: '/adbc-drivers/gizmosql' },
      { label: 'Oracle Driver', to: '/adbc-drivers/oracle' },
      { label: 'Db2 Driver', to: '/adbc-drivers/db2' },
      { label: 'DuckDB Quack Driver', to: '/adbc-drivers/quack' },
    ],
  },
  {
    heading: 'Solutions',
    links: [
      { label: 'Cut Your Warehouse Bill', to: '/cut-your-warehouse-bill' },
      { label: 'DuckDB Server', to: '/duckdb-sql-server' },
      { label: 'Apache Arrow Flight SQL', to: '/arrow-flight-sql' },
    ],
  },
  {
    heading: 'Services',
    links: [{ label: 'Professional Services', to: '/services' }],
  },
  {
    // GizmoSQL 1.x is in maintenance; Hardcharger is its successor. Every URL stays as it was.
    heading: 'Legacy',
    links: [
      { label: 'GizmoSQL', to: '/gizmosql' },
      { label: 'GizmoSQL Enterprise', to: '/gizmosql/enterprise' },
      { label: 'GizmoSQL UI', to: '/gizmosql/ui' },
      { label: 'GizmoSQL MCP Server', to: '/gizmosql/mcp' },
      { label: 'GizmoSQL Integrations', to: '/gizmosql/integrations' },
    ],
  },
];

// "Resources" groups editorial content with the campaign comparison/use-case pages.
const resourceGroups: { heading: string; links: NavLink[] }[] = [
  {
    heading: 'Learn',
    links: [
      { label: 'Blog', to: '/blog' },
      { label: 'News', to: '/news' },
    ],
  },
  {
    heading: 'Comparisons',
    links: [
      { label: 'Hardcharger vs Snowflake', to: '/hardcharger/vs-snowflake' },
      { label: 'Hardcharger vs Databricks', to: '/hardcharger/vs-databricks' },
      { label: 'Hardcharger vs BigQuery', to: '/hardcharger/vs-bigquery' },
    ],
  },
  {
    heading: 'Use Cases',
    links: [
      { label: 'Hardcharger for AI Agents', to: '/hardcharger/ai-agents' },
      { label: 'Offload Expensive Workloads', to: '/hardcharger/offload' },
      { label: 'Cut Your BigQuery Bill', to: '/cut-your-bigquery-bill' },
    ],
  },
];

const companyLinks: NavLink[] = [
  { label: 'About', to: '/about' },
  { label: 'Customers', to: '/customers' },
  { label: 'Case Studies', to: '/case-studies' },
  { label: 'Partners', to: '/partners' },
  { label: 'Careers', to: '/careers' },
  { label: 'Contact', to: '/contact?from=navbar' },
];

const triggerClass =
  'flex items-center gap-1 text-muted-foreground hover:text-primary focus:text-primary transition-colors duration-200 focus:outline-none';
const linkClass =
  'text-muted-foreground hover:text-primary focus:text-primary transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded';

// GizmoSQL 1.x pages point to its successor. Always rendered as an element (it returns null elsewhere): a
// conditional `false` child would be dropped by the Portuguese tree translation and shift React's useId values.
const LegacyGizmoSQLBanner = () => {
  const location = useLocation();
  const legacyPaths = ['/gizmosql', '/gizmosql/enterprise', '/gizmosql/install', '/gizmosql/ui', '/gizmosql/mcp', '/gizmosql/integrations'];
  if (!legacyPaths.includes(location.pathname.replace(/\/$/, ''))) return null;
  return <Localize messages={sharedPortuguese}>{(
    <div className="border-b border-hc-cobalt/30 bg-hc-cobalt/10 text-sm text-foreground" role="note">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center">
        <span>GizmoSQL is in maintenance; Hardcharger™ is its successor.</span>
        <Link to="/hardcharger" className="font-semibold text-hc-cobalt underline-offset-4 hover:underline dark:text-hc-azure" data-ga-cta="explore_hardcharger" data-ga-location="gizmosql_legacy_banner">Meet Hardcharger</Link>
        <span aria-hidden="true">·</span>
        <LocaleAnchor href={HARDCHARGER_MIGRATION_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-hc-cobalt underline-offset-4 hover:underline dark:text-hc-azure" data-ga-cta="view_migration_guide" data-ga-location="gizmosql_legacy_banner">Migration guide</LocaleAnchor>
      </div>
    </div>
  )}</Localize>;
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const getNavLink = (sectionId: string) =>
    location.pathname === '/' ? `#${sectionId}` : `/#${sectionId}`;

  const scrollToTop = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const close = () => setIsMenuOpen(false);

  const mobileLinkClass =
    'block px-3 py-2 rounded-md text-base font-medium text-muted-foreground hover:text-primary hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary focus:bg-muted transition-colors';

  return <Localize messages={sharedPortuguese}>{(
    <>
    <AnnouncementBar />
    <LegacyGizmoSQLBanner />
    <nav
      className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border shadow-sm"
      role="navigation"
      aria-label="Site navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
              onClick={scrollToTop}
              aria-label="GizmoData Home"
            >
              <LocaleImage src="/gizmodata-logo.png" alt="GizmoData Logo" className="h-8 w-auto" width="32" height="32" />
              <span className="text-2xl font-bold text-foreground">GizmoData</span>
            </Link>
          </div>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-5 2xl:gap-6 whitespace-nowrap" role="navigation" aria-label="Main navigation">
            <Link to="/" onClick={scrollToTop} className={linkClass}>
              Home
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger className={triggerClass}>
                Products <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto">
                {productGroups.map((group, i) => (
                  <React.Fragment key={group.heading}>
                    {i > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                      {group.heading}
                    </DropdownMenuLabel>
                    {group.links.map((l) => (
                      <DropdownMenuItem asChild key={l.to}>
                        <Link to={l.to}>{l.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </React.Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={triggerClass}>
                Resources <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto">
                {resourceGroups.map((group, i) => (
                  <React.Fragment key={group.heading}>
                    {i > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                      {group.heading}
                    </DropdownMenuLabel>
                    {group.links.map((l) => (
                      <DropdownMenuItem asChild key={l.to}>
                        <Link to={l.to}>{l.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </React.Fragment>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className={triggerClass}>
                Company <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {companyLinks.map((l) => (
                  <DropdownMenuItem asChild key={l.to}>
                    <Link to={l.to}>{l.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <LocaleAnchor
              href={HARDCHARGER_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              Docs
            </LocaleAnchor>
            <LanguageSelector />
            <ThemeToggle />
            <LocaleAnchor
              href={CLOUD_APP_URL}
              className={cn(linkClass, 'hidden lg:inline')}
              data-ga-cta="cloud_sign_in"
              data-ga-location="navbar"
            >
              Sign in
            </LocaleAnchor>
            <LocaleAnchor href={CLOUD_SIGNUP_URL}>
              <Button
                size="sm"
                className="focus:ring-2 focus:ring-primary focus:ring-offset-2"
                data-ga-cta="cloud_signup"
                data-ga-location="navbar"
              >
                Start free
              </Button>
            </LocaleAnchor>
          </nav>

          <div className="flex xl:hidden items-center space-x-2">
            <ThemeToggle />
            <button
              className="inline-flex items-center justify-center p-2 rounded-md text-muted-foreground hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        id="mobile-menu"
        className={cn('xl:hidden', isMenuOpen ? 'block animate-fade-in' : 'hidden animate-fade-out')}
      >
        <nav
          className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-card shadow-lg max-h-[80vh] overflow-y-auto"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="px-3 py-2"><LanguageSelector /></div>
          <Link
            to="/"
            className={mobileLinkClass}
            onClick={(e) => {
              close();
              scrollToTop(e);
            }}
          >
            Home
          </Link>

          {productGroups.map((group) => (
            <div key={group.heading}>
              <div className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.heading}
              </div>
              {group.links.map((l) => (
                <Link key={l.to} to={l.to} className={mobileLinkClass} onClick={close}>
                  {l.label}
                </Link>
              ))}
            </div>
          ))}

          {resourceGroups.map((group) => (
            <div key={group.heading}>
              <div className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.heading}
              </div>
              {group.links.map((l) => (
                <Link key={l.to} to={l.to} className={mobileLinkClass} onClick={close}>
                  {l.label}
                </Link>
              ))}
            </div>
          ))}

          <div className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Company
          </div>
          {companyLinks.map((l) => (
            <Link key={l.to} to={l.to} className={mobileLinkClass} onClick={close}>
              {l.label}
            </Link>
          ))}

          <LocaleAnchor
            href={HARDCHARGER_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={mobileLinkClass}
            onClick={close}
          >
            Docs
          </LocaleAnchor>

          <div className="grid grid-cols-2 gap-2 px-3 pt-3">
            <LocaleAnchor href={CLOUD_APP_URL} onClick={close} data-ga-cta="cloud_sign_in" data-ga-location="navbar_mobile">
              <Button variant="outline" className="w-full">Sign in</Button>
            </LocaleAnchor>
            <LocaleAnchor href={CLOUD_SIGNUP_URL} onClick={close} data-ga-cta="cloud_signup" data-ga-location="navbar_mobile">
              <Button className="w-full">Start free</Button>
            </LocaleAnchor>
          </div>
        </nav>
      </div>
    </nav>
    </>
  )}</Localize>;
};

export default Navbar;
